import {
  Modal,
  Stack,
  Text,
  List,
  ThemeIcon,
  Alert,
  Button,
  Group,
  Code,
  Divider,
  Badge,
} from '@mantine/core';
import {
  IconKey,
  IconInfoCircle,
  IconUserCheck,
  IconAlertTriangle,
  IconExternalLink,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

/**
 * Erklärt Technikern, warum ihre Einträge unter fremdem Namen erscheinen und wie
 * sie das mit einem eigenen weclapp-Token abstellen. Bewusst in Alltagssprache:
 * Adressat ist der Techniker im Feld, nicht der Administrator.
 */
export function WeclappTokenTutorial({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();

  // Web-Root der eigenen weclapp-Instanz — der Direktlink führt damit ohne Sucherei
  // genau auf die Seite mit dem API-Token-Abschnitt.
  const { data } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => api.getMe(),
    staleTime: 300_000,
  });
  const settingsUrl = data?.user?.weclappWebRoot
    ? `${data.user.weclappWebRoot}/app/my-settings`
    : null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <ThemeIcon variant="light" color="yellow" size="sm">
            <IconKey size={14} />
          </ThemeIcon>
          <Text fw={600}>weclapp-Token hinterlegen</Text>
        </Group>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm">
            Solange kein eigener Token hinterlegt ist, laufen deine Kommentare und
            Zeitbuchungen über den gemeinsamen Portal-Zugang. In weclapp steht dann
            <strong> nicht dein Name</strong>, sondern der des Portal-Benutzers.
          </Text>
        </Alert>

        <div>
          <Text fw={500} mb={4}>
            Was sich mit eigenem Token ändert
          </Text>
          <List
            spacing={4}
            size="sm"
            icon={
              <ThemeIcon color="teal" size={18} radius="xl">
                <IconUserCheck size={12} />
              </ThemeIcon>
            }
          >
            <List.Item>Kommentare erscheinen in weclapp unter deinem Namen.</List.Item>
            <List.Item>
              Zeitbuchungen laufen auf dein Konto — wichtig für Auswertungen und Abrechnung.
            </List.Item>
            <List.Item>
              Der Zusatz „[FixByte ServiceHub – Name]" im Text entfällt.
            </List.Item>
          </List>
        </div>

        <Divider />

        <div>
          <Text fw={500} mb={6}>
            So bekommst du deinen Token
          </Text>
          <List type="ordered" spacing={6} size="sm">
            <List.Item>
              {settingsUrl ? (
                <>
                  Die weclapp-Einstellungen öffnen (Button unten) — du landest direkt auf{' '}
                  <Code>Meine Einstellungen</Code>. Wichtig: mit <strong>deinem eigenen</strong>{' '}
                  weclapp-Benutzer angemeldet sein.
                </>
              ) : (
                <>
                  In weclapp anmelden und oben rechts über dein Profilbild{' '}
                  <Code>Meine Einstellungen</Code> öffnen.
                </>
              )}
            </List.Item>
            <List.Item>
              Auf der Seite nach unten zum Abschnitt <Code>API-Token</Code> scrollen.
            </List.Item>
            <List.Item>
              <Code>+ Neuen API-Token hinzufügen</Code> klicken und als Beschreibung z. B.{' '}
              <Code>TechnikerPortal</Code> eintragen.
            </List.Item>
            <List.Item>Den erzeugten Token vollständig kopieren.</List.Item>
            <List.Item>
              Hier im Portal unter <Code>Mein Profil → Mein weclapp-Zugang</Code> einfügen
              und speichern.
            </List.Item>
          </List>
        </div>

        {settingsUrl && (
          <Button
            component="a"
            href={settingsUrl}
            target="_blank"
            rel="noreferrer"
            variant="light"
            leftSection={<IconExternalLink size={16} />}
            fullWidth
          >
            weclapp-Einstellungen öffnen (API-Token)
          </Button>
        )}

        <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
          <Text size="sm">
            Der Token wird verschlüsselt gespeichert und niemandem angezeigt — auch dir nicht
            mehr. Du kannst ihn jederzeit ersetzen oder entfernen. Findest du den Abschnitt in
            weclapp nicht, fehlt eventuell die Berechtigung: dann kurz bei der
            Administration melden.
          </Text>
        </Alert>

        <Group justify="space-between">
          <Badge variant="light" color="gray">
            Dauert ca. 2 Minuten
          </Badge>
          <Group gap="sm">
            <Button variant="default" onClick={onClose}>
              Später
            </Button>
            <Button
              leftSection={<IconKey size={16} />}
              onClick={() => {
                onClose();
                navigate('/profile');
              }}
            >
              Jetzt eintragen
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
